import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ProcessPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { ToastService } from "../../service/toast.service";
import { useLanguage } from "../../context/LanguageContext";
import { processApi, Process } from "../../helper/processApi";

interface ProcessWithActions {
  process: Process;
  actionsCount: number;
}

export default function ProcessPage() {
  const [processes, setProcesses] = useState<ProcessWithActions[]>([]);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [newProcessName, setNewProcessName] = useState("");
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    loadProcesses();
  }, []);

  const loadProcesses = async () => {
    try {
      const allProcesses = await processApi.getAll();

      const allActions = await processApi.getAllActions();

      const actionCounts = new Map<number, number>();
      allActions.forEach((action) => {
        actionCounts.set(
          action.processId,
          (actionCounts.get(action.processId) || 0) + 1
        );
      });

      const processesWithActions: ProcessWithActions[] = allProcesses.map(
        (process) => ({
          process,
          actionsCount: actionCounts.get(process.id) || 0,
        })
      );

      setProcesses(processesWithActions);
    } catch (e) {
      console.error("Error loading processes:", e);
      ToastService.error(t("loadError") || "Load error");
    }
  };

  const handleCreateProcess = async () => {
    try {
      if (!newProcessName.trim()) {
        ToastService.error(t("nameRequired") || "Name is required");
        return;
      }

      const newProcess = await processApi.create(newProcessName);
      
      ToastService.success(t("processCreated") || "Process created");
      setShowCreateCard(false);
      setNewProcessName("");
      await loadProcesses();
      
      navigate(`/process-action/${newProcess.id}`);
    } catch (e) {
      console.error("Error creating process:", e);
      ToastService.error(t("createError") || "Create error");
    }
  };

  const handleCancelCreate = () => {
    setShowCreateCard(false);
    setNewProcessName("");
  };

  const handleDeleteProcess = async (processId: number) => {
    if (!confirm(t("confirmDeleteProcess") || "Delete this process?")) {
      return;
    }

    try {
      await processApi.delete(processId);
      ToastService.success(t("processDeleted") || "Process deleted");
      await loadProcesses();
    } catch (e) {
      console.error("Error deleting process:", e);
      ToastService.error(t("deleteError") || "Delete error");
    }
  };

  const handleOpenProcess = (processId: number) => {
    navigate(`/process-action/${processId}`);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t("process") || "Processes"}</h1>
      </div>

      <div className={styles.processGrid}>
        {processes.map(({ process, actionsCount }) => (
          <div
            key={process.id}
            className={styles.processCard}
            onClick={() => handleOpenProcess(process.id)}
          >
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>
                {process.name || `Process #${process.id}`}
              </h3>
              <div className={styles.cardActions}>
                <button
                  className={styles.iconButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProcess(process.id);
                  }}
                  title={t("delete") || "Delete"}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>

            <div className={styles.cardFooter}>
              <span className={styles.processIdBadge}>ID: {process.id}</span>
              <span className={styles.actionsCount}>
                {actionsCount} {t("actions") || "actions"}
              </span>
              <FontAwesomeIcon icon={faEdit} className={styles.editIcon} />
            </div>
          </div>
        ))}

        {showCreateCard ? (
          <div className={`${styles.processCard} ${styles.newCard}`}>
            <div className={styles.newCardHeader}>
              <h3>{t("newProcess") || "New Process"}</h3>
            </div>

            <div className={styles.newCardForm}>
              <input
                type="text"
                className={styles.input}
                placeholder={t("processName") || "Process name"}
                value={newProcessName}
                onChange={(e) => setNewProcessName(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleCreateProcess();
                  } else if (e.key === "Escape") {
                    handleCancelCreate();
                  }
                }}
              />
            </div>

            <div className={styles.newCardActions}>
              <button
                className={styles.saveButton}
                onClick={handleCreateProcess}
                title={t("save") || "Save"}
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
              <button
                className={styles.cancelButton}
                onClick={handleCancelCreate}
                title={t("cancel") || "Cancel"}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`${styles.processCard} ${styles.createCard}`}
            onClick={() => setShowCreateCard(true)}
          >
            <FontAwesomeIcon icon={faPlus} className={styles.createIcon} />
            <span>{t("createProcess") || "Create Process"}</span>
          </div>
        )}
      </div>

    </div>
  );
}